import { z } from "zod";
import type { JSONSchemaDefinition } from "../jsonSchema";
import { ERP_PROVIDER_IDS, type ERPProviderKey } from "./constants";
import { ConnectionProviderKeySchema } from "../schemas/connections";

export const ERPProviderKeySchema = z.enum(ERP_PROVIDER_IDS);

export const ERPIntegrationSyncStatusSchema = z.enum(["ENABLED", "DISABLED"]);

/**
 * Shared sync configuration schema for ERP integration providers. Providers use
 * this as their `syncConfigSchema` (rendered as toggles in the UI) and to parse
 * `integrationConfig` at pull/push time to decide which entity types to sync.
 *
 * Pull toggles (`syncPeople`, `syncCompanies`, `syncOpportunities`,
 * `syncPriceLists`, `syncItems`, `syncOperations`, `syncLocations`,
 * `syncInventoryLines`) control inbound sync. Push
 * toggles (`pushCompanies`, `pushPeople`, `pushOpportunities`, `pushQuotes`)
 * control outbound sync. Every toggle defaults to `false`, so a missing or
 * undefined setting never syncs until it is explicitly enabled.
 */
export const ERPSyncConfigSchema = z.object({
  syncPeople: z
    .boolean()
    .default(false)
    .meta({ title: "Pull People", description: "Pull people from this integration" }),
  syncCompanies: z
    .boolean()
    .default(false)
    .meta({ title: "Pull Companies", description: "Pull companies from this integration" }),
  syncOpportunities: z
    .boolean()
    .default(false)
    .meta({
      title: "Pull Opportunities",
      description:
        "Pull updates for opportunities created in Forgepoint from this integration",
    }),
  syncPriceLists: z
    .boolean()
    .default(false)
    .meta({
      title: "Pull Price Lists",
      description:
        "Pull price lists and their customer assignments from this integration",
    }),
  syncItems: z.boolean().default(false).meta({
    title: "Pull Items",
    description:
      "Pull parts and their approved revisions from this integration",
  }),
  syncOperations: z.boolean().default(false).meta({
    title: "Pull Operations",
    description: "Pull operations and resource rates from this integration",
  }),
  syncLocations: z.boolean().default(false).meta({
    title: "Pull Locations",
    description: "Pull locations from this integration",
  }),
  syncInventoryLines: z.boolean().default(false).meta({
    title: "Pull Inventory",
    description:
      "Pull available stock for pulled items and locations from this integration",
  }),
  pushCompanies: z
    .boolean()
    .default(false)
    .meta({ title: "Push Companies", description: "Push companies to this integration" }),
  pushPeople: z
    .boolean()
    .default(false)
    .meta({ title: "Push People", description: "Push people to this integration" }),
  pushOpportunities: z
    .boolean()
    .default(false)
    .meta({ title: "Push Opportunities", description: "Push opportunities to this integration" }),
  pushQuotes: z.boolean().default(false).meta({
    title: "Push Quotes",
    description:
      "Push finalized quotes, and the items they depend on, to this integration",
  }),
});

export type ERPSyncConfig = z.infer<typeof ERPSyncConfigSchema>;

/**
 * Opt-in draft quote push. Extended into the sync config of providers that can
 * rewrite a quote they already pushed, so it renders under Configuration rather
 * than as a capability-gated sync toggle.
 */
export const DraftQuotePushConfigSchema = z.object({
  pushDraftQuotes: z.boolean().default(false).meta({
    title: "Push draft quotes",
    description: "Push draft quote header only",
  }),
});

/**
 * Display order for the base ERP sync config properties. Added to the JSON
 * Schema as `propertyOrder` so the frontend can render config rows in a
 * consistent order without maintaining a separate hardcoded list.
 */
export const ERP_SYNC_CONFIG_PROPERTY_ORDER: string[] = [
  "syncCompanies",
  "syncPeople",
  "syncOpportunities",
  "syncPriceLists",
  "syncItems",
  "syncOperations",
  "syncLocations",
  "syncInventoryLines",
  "pushCompanies",
  "pushPeople",
  "pushOpportunities",
  "pushQuotes",
];

/**
 * Explicit list of push entity keys in the sync config.
 * Update this array when adding a new push toggle to `ERPSyncConfigSchema`.
 */
export const PUSH_ENTITY_KEYS = [
  "pushCompanies",
  "pushPeople",
  "pushOpportunities",
  "pushQuotes",
] as const satisfies readonly (keyof ERPSyncConfig)[];

/**
 * Returns whether at least one push entity type is enabled in the sync config.
 * @param config - The parsed ERP sync config
 * @returns Whether any push entity is enabled
 */
export function hasAnyPushEntityEnabled(config: ERPSyncConfig): boolean {
  return PUSH_ENTITY_KEYS.some((key) => config[key]);
}

/** An option on an ERP enumeration property. */
export interface ErpPropertyOption {
  label: string;
  value: string;
}

/**
 * Extends `JSONSchemaDefinition` with ERP-specific extensions
 * added by the backend for select custom field mapping schemas.
 */
export type ERPSchemaDefinition = JSONSchemaDefinition & {
  /** Whether this field is a select type requiring option-level mapping. */
  isSelectField?: boolean;
  /** FP select option values for this field. */
  fpFieldOptions?: string[];
  /** ERP property options keyed by ERP property name. */
  erpPropertyOptions?: Record<string, ErpPropertyOption[]>;
  /** Provider-supplied hint messages per ERP field, keyed by field API name. */
  fieldHints?: Record<string, string>;
};

/**
 * Schema for a select custom field mapping that includes per-option value
 * mappings from FP option labels to ERP enumeration internal values.
 */
export const SelectFieldMappingSchema = z.object({
  erpProperty: z.string().min(1),
  optionMapping: z.record(z.string(), z.string()),
});

/** A select custom field mapping with per-option value mappings. */
export type SelectFieldMapping = z.infer<typeof SelectFieldMappingSchema>;

/**
 * Reusable schema for standard opportunity field mapping (FP field → ERP field).
 * Each provider applies its own `.meta()` for provider-specific UI labels.
 */
export const OpportunityFieldMappingSchema = z
  .record(z.string(), z.string())
  .optional();

/**
 * Reusable schema for custom field mapping with optional per-option mapping
 * for select fields. Each provider applies its own `.meta()` for
 * provider-specific UI labels.
 */
export const OpportunityCustomFieldMappingSchema = z
  .record(z.string(), z.union([z.string(), SelectFieldMappingSchema]))
  .optional();

const JsonRecordSchema = z.record(z.string(), z.unknown());

export const RequiredConnectionProviderSchema = z.object({
  providerId: ConnectionProviderKeySchema,
  appId: z.string(),
});

export const ERPIntegrationProviderMetadataSchema = z.object({
  id: ERPProviderKeySchema,
  name: z.string(),
  description: z.string().nullish(),
  syncConfigSchema: z.unknown().nullish(),
  requiredConnectionProvider: RequiredConnectionProviderSchema,
});

export const ERPIntegrationSyncRecordSchema = z.object({
  id: z.string(),
  erpIntegrationProviderId: ERPProviderKeySchema,
  connectionId: z.string(),
  status: ERPIntegrationSyncStatusSchema,
  integrationConfig: JsonRecordSchema.nullish(),
  autoPushEnabled: z.boolean().default(false),
  lastPushScannedAt: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ERPIntegrationSyncConnectionSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  appId: z.string(),
  connectionProviderId: ConnectionProviderKeySchema,
});

export const ERPIntegrationSyncWithConnectionSchema = z.object({
  sync: ERPIntegrationSyncRecordSchema,
  connection: ERPIntegrationSyncConnectionSchema,
});

export const ERPIntegrationSyncSchema = ERPIntegrationSyncRecordSchema.extend({
  connection: ERPIntegrationSyncConnectionSchema,
  provider: ERPIntegrationProviderMetadataSchema.nullish(),
});

export const ERPIntegrationProvidersResponseSchema = z.object({
  providers: z.array(ERPIntegrationProviderMetadataSchema),
});

export const ERPIntegrationSyncsResponseSchema = z.object({
  syncs: z.array(ERPIntegrationSyncSchema),
});

export const ERPIntegrationSyncResponseSchema = z.object({
  sync: ERPIntegrationSyncSchema,
});

/**
 * Runtime capabilities for a specific ERP connection. Declares which
 * operations the connection can currently perform based on granted
 * OAuth scopes or provider-specific authorization. Used by the scanner
 * to avoid enqueueing work that will be skipped, and by the frontend
 * to disable toggles for unavailable operations.
 */
export const ConnectionCapabilitiesSchema = z.object({
  canPullCompanies: z.boolean(),
  canPushCompanies: z.boolean(),
  canPullPeople: z.boolean(),
  canPushPeople: z.boolean(),
  canPullOpportunities: z.boolean(),
  canPushOpportunities: z.boolean(),
  canPullPriceLists: z.boolean(),
  canPullItems: z.boolean(),
  canPullOperations: z.boolean(),
  canPullLocations: z.boolean(),
  canPullInventoryLines: z.boolean(),
  canPushQuotes: z.boolean(),
  canResolveOwners: z.boolean(),
  canPushAttachments: z.boolean(),
});

/** Runtime capability flags for a specific ERP connection. */
export type ConnectionCapabilities = z.infer<
  typeof ConnectionCapabilitiesSchema
>;

/**
 * Default capabilities - all operations enabled, except price list, item,
 * operation, location and inventory pull and quote push, which a provider must
 * opt into by implementing `pullPriceLists`, `pullItems`, `pullOperations`,
 * `pullLocations`, `pullInventoryLines` or `pushQuote`.
 */
export const DEFAULT_CONNECTION_CAPABILITIES: ConnectionCapabilities = {
  canPullCompanies: true,
  canPushCompanies: true,
  canPullPeople: true,
  canPushPeople: true,
  canPullOpportunities: true,
  canPushOpportunities: true,
  canPullPriceLists: false,
  canPullItems: false,
  canPullOperations: false,
  canPullLocations: false,
  canPullInventoryLines: false,
  canPushQuotes: false,
  canResolveOwners: true,
  canPushAttachments: true,
};

export const ResolvedSyncConfigSchemaResponseSchema = z.object({
  schema: z.record(z.string(), z.unknown()).nullable(),
  canScan: z.boolean().optional(),
  capabilities: ConnectionCapabilitiesSchema.optional(),
});

export type ERPIntegrationSyncStatus = z.infer<
  typeof ERPIntegrationSyncStatusSchema
>;
export type RequiredConnectionProvider = z.infer<
  typeof RequiredConnectionProviderSchema
>;
export type ERPIntegrationProviderMetadata = z.infer<
  typeof ERPIntegrationProviderMetadataSchema
>;
export type ERPIntegrationSyncRecord = z.infer<
  typeof ERPIntegrationSyncRecordSchema
>;
export type ERPIntegrationSyncWithConnection = z.infer<
  typeof ERPIntegrationSyncWithConnectionSchema
>;
export type ERPIntegrationSync = z.infer<typeof ERPIntegrationSyncSchema>;
export type ERPIntegrationProvidersResponse = z.infer<
  typeof ERPIntegrationProvidersResponseSchema
>;
export type ERPIntegrationSyncsResponse = z.infer<
  typeof ERPIntegrationSyncsResponseSchema
>;
export type ERPIntegrationSyncResponse = z.infer<
  typeof ERPIntegrationSyncResponseSchema
>;
export type ResolvedSyncConfigSchemaResponse = z.infer<
  typeof ResolvedSyncConfigSchemaResponseSchema
>;

export type { ERPProviderKey };

// ---------------------------------------------------------------------------
// Opportunity field mapping — shared across all ERP providers
// ---------------------------------------------------------------------------

/** Field type descriptors for opportunity field mapping resolution. */
export type OpportunityMappableFieldType =
  | "user"
  | "users"
  | "amount"
  | "percentage"
  | "date";

/**
 * Metadata for a single mappable opportunity field.
 */
export interface OpportunityMappableFieldDef {
  type: OpportunityMappableFieldType;
  label: string;
}

/**
 * The set of built-in Opportunity fields that can be mapped to any ERP's
 * target properties. Defined once here so every ERP provider references the
 * same field list and type descriptors without duplication.
 */
export const OPPORTUNITY_MAPPABLE_FIELDS: Record<
  string,
  OpportunityMappableFieldDef
> = {
  salesRepUserId: { type: "user", label: "Sales Rep" },
  estimatorUserIds: { type: "users", label: "Estimators" },
  amount: { type: "amount", label: "Amount" },
  probability: { type: "percentage", label: "Probability" },
  closedAt: { type: "date", label: "Closed Date" },
  firstQuoteSentAt: { type: "date", label: "First Quote Sent" },
  firstQuoteInProgressAt: { type: "date", label: "First Quote In Progress" },
  createdAt: { type: "date", label: "Created" },
  archivedAt: { type: "date", label: "Archived" },
} as const;
